import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import {
  FEATURE_KEYS,
  FEATURE_LABEL,
  PLAN_LIMIT_KEYS,
  PLAN_LIMIT_LABEL,
  platformPlanInput,
  type PlatformFeatureFlag,
  type PlatformPlan,
} from '@barbeiropro/contracts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { PageHeader } from '@/components/shared/page-header';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { Pill } from '@/features/finance/pill';
import { api, errorMessage } from '@/lib/api';
import { centsToInput, money, parseMoney } from '@/lib/format';
import { usePlans } from './orgs-page';
import { Card, IconAction, INTERVAL_LABEL, usePlatformAbility } from './shared';

const formSchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string(),
  price: z.string().min(1, 'Informe o preço'),
  interval: z.enum(['month', 'year']),
  trialDays: z.string(),
  sort: z.string(),
  active: z.boolean(),
  isPublic: z.boolean(),
  limits: z.record(z.string(), z.string()),
  features: z.record(z.string(), z.boolean()),
});
type FormValues = z.infer<typeof formSchema>;

const toPayload = (v: FormValues) => ({
  key: v.key,
  name: v.name,
  description: v.description,
  active: v.active,
  isPublic: v.isPublic,
  priceCents: parseMoney(v.price),
  currency: 'BRL' as const,
  interval: v.interval,
  trialDays: Number(v.trialDays || 0),
  sort: Number(v.sort || 0),
  limits: Object.fromEntries(PLAN_LIMIT_KEYS.map((k) => [k, v.limits[k]?.trim() ? Number(v.limits[k]) : null])),
  features: v.features,
});

function PlanModal({ plan, onClose }: { plan: PlatformPlan | 'new'; onClose: () => void }) {
  const qc = useQueryClient();
  const editing = plan === 'new' ? null : plan;
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      key: editing?.key ?? '',
      name: editing?.name ?? '',
      description: editing?.description ?? '',
      price: centsToInput(editing?.priceCents ?? 0),
      interval: editing?.interval ?? 'month',
      trialDays: String(editing?.trialDays ?? 0),
      sort: String(editing?.sort ?? 0),
      active: editing?.active ?? true,
      isPublic: editing?.isPublic ?? true,
      limits: Object.fromEntries(PLAN_LIMIT_KEYS.map((k) => [k, editing?.limits[k] == null ? '' : String(editing.limits[k])])),
      features: Object.fromEntries(FEATURE_KEYS.map((k) => [k, editing?.features[k] ?? true])),
    },
  });
  const save = useMutation({
    mutationFn: (payload: unknown) => (editing ? api.put(`/platform/plans/${editing.id}`, payload) : api.post('/platform/plans', payload)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform'] });
      toast.success('Plano salvo');
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const submit = form.handleSubmit((v) => {
    const parsed = platformPlanInput.safeParse(toPayload(v));
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      toast.error(`${issue?.path.join('.') ?? 'Plano'}: ${issue?.message ?? 'valor inválido'}`);
      return;
    }
    save.mutate(parsed.data);
  });
  const e = form.formState.errors;
  return (
    <FormModal
      open
      onOpenChange={(o) => !o && onClose()}
      size="lg"
      title={editing ? `Editar plano ${editing.name}` : 'Novo plano'}
      description="Preço, limites e recursos são editáveis a qualquer momento. Deixe um limite vazio para ilimitado."
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" form="plan-edit" disabled={save.isPending}>Salvar plano</Button>
        </>
      }
    >
      <form id="plan-edit" onSubmit={submit} noValidate className="grid gap-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="p-name" label="Nome" error={e.name?.message}><Input id="p-name" {...form.register('name')} /></Field>
          <Field id="p-key" label="Chave" hint="Identificador interno (a-z, 0-9, hífen)."><Input id="p-key" {...form.register('key')} /></Field>
          <Field id="p-price" label="Preço (R$)" error={e.price?.message}><Input id="p-price" inputMode="decimal" {...form.register('price')} /></Field>
          <Field id="p-interval" label="Cobrança">
            <Select value={form.watch('interval')} onValueChange={(v) => form.setValue('interval', v as 'month' | 'year')}>
              <SelectTrigger id="p-interval" className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Mensal</SelectItem>
                <SelectItem value="year">Anual</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field id="p-trial" label="Dias de teste"><Input id="p-trial" type="number" min={0} {...form.register('trialDays')} /></Field>
          <Field id="p-sort" label="Ordem de exibição"><Input id="p-sort" type="number" min={0} {...form.register('sort')} /></Field>
        </div>
        <Field id="p-desc" label="Descrição"><Textarea id="p-desc" rows={2} {...form.register('description')} /></Field>
        <div className="flex flex-wrap gap-6 text-sm">
          <label className="flex items-center gap-2"><Switch checked={form.watch('active')} onCheckedChange={(c) => form.setValue('active', c)} aria-label="Plano ativo" /> Ativo</label>
          <label className="flex items-center gap-2"><Switch checked={form.watch('isPublic')} onCheckedChange={(c) => form.setValue('isPublic', c)} aria-label="Plano público" /> Visível para as barbearias</label>
        </div>
        <fieldset className="grid gap-3">
          <legend className="mb-1 text-sm font-semibold">Limites</legend>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PLAN_LIMIT_KEYS.map((k) => (
              <Field key={k} id={`lim-${k}`} label={PLAN_LIMIT_LABEL[k]}>
                <Input id={`lim-${k}`} type="number" min={0} placeholder="Ilimitado" {...form.register(`limits.${k}`)} />
              </Field>
            ))}
          </div>
        </fieldset>
        <fieldset className="grid gap-2">
          <legend className="mb-1 text-sm font-semibold">Recursos incluídos</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {FEATURE_KEYS.map((k) => (
              <label key={k} className="flex items-center gap-2 text-sm">
                <Switch checked={form.watch(`features.${k}`)} onCheckedChange={(c) => form.setValue(`features.${k}`, c)} aria-label={FEATURE_LABEL[k]} /> {FEATURE_LABEL[k]}
              </label>
            ))}
          </div>
        </fieldset>
      </form>
    </FormModal>
  );
}

function FlagsCard({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['platform', 'flags'], queryFn: () => api.get<PlatformFeatureFlag[]>('/platform/feature-flags') });
  const toggle = useMutation({
    mutationFn: (f: PlatformFeatureFlag) => api.put(`/platform/feature-flags/${f.key}`, { defaultEnabled: !f.defaultEnabled, description: f.description }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'flags'] }),
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Card title="Feature flags" description="Valor padrão de cada recurso quando o plano não define nada e não há sobrescrita da organização.">
      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} onRetry={q.refetch} /> : (
        <ul className="divide-y">
          {(q.data ?? []).map((f) => (
            <li key={f.key} className="flex items-center justify-between gap-3 py-2 text-sm">
              <span><span className="font-medium">{FEATURE_LABEL[f.key]}</span>{f.description && <span className="block text-xs text-muted-foreground">{f.description}</span>}</span>
              <Switch checked={f.defaultEnabled} disabled={!canEdit || toggle.isPending} onCheckedChange={() => toggle.mutate(f)} aria-label={`Padrão de ${FEATURE_LABEL[f.key]}`} />
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function PlansPage() {
  const plans = usePlans();
  const { isOwner } = usePlatformAbility();
  const [editing, setEditing] = useState<PlatformPlan | 'new' | null>(null);
  return (
    <div className="grid grid-cols-1 gap-6">
      <PageHeader
        title="Planos"
        description="Preços e limites são definidos aqui; nada é fixo no código. Planos não são apagados, apenas desativados."
        actions={isOwner && <Button onClick={() => setEditing('new')}><Plus className="size-4" /> Novo plano</Button>}
      />
      {plans.isLoading ? <LoadingState /> : plans.error ? <ErrorState error={plans.error} onRetry={plans.refetch} /> : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {(plans.data ?? []).map((p) => (
            <Card key={p.id} className="flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h2 className="truncate text-lg font-semibold">{p.name}</h2>
                  <p className="font-display text-2xl font-bold tabular">{money(p.priceCents)}<span className="text-sm font-normal text-muted-foreground">/{INTERVAL_LABEL[p.interval]}</span></p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Pill tone={p.active ? 'good' : 'neutral'}>{p.active ? 'Ativo' : 'Inativo'}</Pill>
                  {!p.isPublic && <Pill>Oculto</Pill>}
                </div>
              </div>
              {p.description && <p className="mt-2 text-sm text-muted-foreground">{p.description}</p>}
              <ul className="mt-3 grid gap-1 text-sm">
                {PLAN_LIMIT_KEYS.map((k) => (
                  <li key={k} className="flex justify-between gap-3"><span className="text-muted-foreground">{PLAN_LIMIT_LABEL[k]}</span><span className="tabular">{p.limits[k] == null ? 'Ilimitado' : p.limits[k]?.toLocaleString('pt-BR')}</span></li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-muted-foreground">
                {FEATURE_KEYS.filter((k) => p.features[k] !== false).map((k) => FEATURE_LABEL[k]).join(', ') || 'Nenhum recurso'}
              </p>
              <div className="mt-auto flex items-center justify-between gap-2 pt-4 text-sm">
                <span className="text-muted-foreground">{p.subscribers} assinante{p.subscribers === 1 ? '' : 's'} · teste de {p.trialDays} dias</span>
                {isOwner && <IconAction label={`Editar plano ${p.name}`} icon={<Pencil className="size-4" aria-hidden />} variant="outline" onClick={() => setEditing(p)} />}
              </div>
            </Card>
          ))}
        </div>
      )}
      <FlagsCard canEdit={isOwner} />
      {editing && <PlanModal plan={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
